/**
 * NotificationContext — Admin App
 * Loads from /admin/notifications API. Polls every 10s.
 * Stops polling if auth fails (401).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getNotifications, markAllNotificationsRead,
  dismissNotification as apiDismiss, clearAllNotifications,
  confirmBookingByAdmin, rejectBookingByAdmin,
  isAuthenticated,
} from '../api';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [panelOpen,     setPanelOpen]     = useState(false);
  const [authError,     setAuthError]     = useState(false); // stop polling on 401

  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated() || authError) return;
    try {
      const data = await getNotifications();
      setNotifications(data || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        setAuthError(true); // stop further polling
        console.warn('Notification poll stopped: auth error');
      }
    }
  }, [authError]);

  /* ── Boot ── */
  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  /* ── Poll every 10s ── */
  useEffect(() => {
    if (authError) return;
    const iv = setInterval(loadNotifications, 10000);
    return () => clearInterval(iv);
  }, [loadNotifications, authError]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = async () => {
    try { await markAllNotificationsRead(); } catch {}
    setNotifications(p => p.map(n => ({ ...n, read: true })));
  };

  const clearAll = async () => {
    try { await clearAllNotifications(); } catch {}
    setNotifications([]);
  };

  const dismiss = async id => {
    try { await apiDismiss(id); } catch {}
    setNotifications(p => p.filter(n => n.id !== id));
  };

  const confirmBooking = async notifId => {
    const notif = notifications.find(n => n.id === notifId);
    const bookingId = notif?.booking?.bookingId || notifId;
    try {
      await confirmBookingByAdmin(bookingId);
      await loadNotifications(); // refresh from DB
    } catch (e) {
      console.error('Confirm failed:', e.response?.data?.message || e.message);
    }
  };

  const rejectBooking = async (notifId, reason) => {
    const notif = notifications.find(n => n.id === notifId);
    const bookingId = notif?.booking?.bookingId || notifId;
    try {
      await rejectBookingByAdmin(bookingId, reason);
      await loadNotifications();
    } catch (e) {
      console.error('Reject failed:', e.response?.data?.message || e.message);
    }
  };

  const togglePanel = () => {
    setPanelOpen(o => !o);
    if (!panelOpen) markAllRead();
  };
  const closePanel = () => setPanelOpen(false);

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, panelOpen,
      togglePanel, closePanel, markAllRead, clearAll, dismiss,
      confirmBooking, rejectBooking,
      refresh: loadNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);